/**
 * VowAI Labs - Floating Widget
 * Left-side floating button that expands to show AI engineering pages:
 * VowAI Labs (Developer Hub), Model Evaluation, A/B Testing
 * All pages open in new tabs
 */
import React, { useState, useRef, useEffect } from 'react';

const VowAILabs = () => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const pages = [
    {
      name: 'VowAI Labs',
      description: 'AI architecture & engineering deep dive',
      icon: '🧬',
      url: '/admin/vowai-labs',
      gradient: 'from-indigo-500 to-purple-600'
    },
    {
      name: 'Model Evaluation',
      description: 'Quality scores, save rates & performance',
      icon: '📊',
      url: '/admin/evaluation',
      gradient: 'from-emerald-500 to-teal-600'
    },
    {
      name: 'A/B Testing',
      description: 'Experiments, significance & effect sizes',
      icon: '🧪',
      url: '/admin/ab-testing',
      gradient: 'from-amber-500 to-orange-600'
    }
  ];

  return (
    <div className="fixed bottom-6 left-6 z-50" ref={panelRef}>
      {/* Expanded panel */}
      {isOpen && (
        <div className="absolute bottom-16 left-0 w-72 bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-indigo-100 overflow-hidden animate-fadeInScale">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 p-4 relative overflow-hidden">
            <div className="absolute top-1 right-2 text-white/20 text-lg animate-pulse">⚡</div>
            <h3 className="font-serif font-bold text-lg text-white">VowAI Labs</h3>
            <p className="text-indigo-200 text-xs font-medium">AI Engineering & Analytics</p>
          </div>

          {/* Navigation cards */}
          <div className="p-3 space-y-2">
            {pages.map((page, idx) => (
              <button
                key={idx}
                onClick={() => { window.open(page.url, '_blank'); setIsOpen(false); }}
                className="w-full group flex items-center p-3 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] bg-gradient-to-r from-gray-50 to-white border border-gray-100 hover:border-indigo-200"
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${page.gradient} flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow duration-300 flex-shrink-0`}>
                  <span className="text-lg">{page.icon}</span>
                </div>
                <div className="ml-3 text-left">
                  <p className="font-bold text-sm text-gray-800 group-hover:text-indigo-700 transition-colors duration-200">{page.name}</p>
                  <p className="text-xs text-gray-500">{page.description}</p>
                </div>
                <svg className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 ml-auto flex-shrink-0 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="group bg-gradient-to-r from-indigo-500 via-purple-500 to-violet-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 flex items-center justify-center animate-pulse"
      >
        {isOpen ? (
          <svg className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <div className="flex items-center space-x-2">
            <span className="text-2xl group-hover:animate-bounce">🧪</span>
            <span className="hidden sm:block font-bold text-lg">Labs</span>
          </div>
        )}
      </button>

      <style jsx>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.8) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fadeInScale { animation: fadeInScale 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default VowAILabs;
